import { FC, useEffect, useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { Avatar, CircularProgress } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import Table from 'src/components/Items/table';
import HostModal from 'src/pages/Admin/Content/Modals/AddHost';
import DeleteModalForm from 'src/components/Items/DeleteModalForm';
import ReviewListModal from '../../Modals/Reviews/List';
import FormIcon from 'src/components/Items/ActionForm/FormIcon';
import { useActions } from 'src/hooks/useActions';
import { useTypedSelector } from 'src/hooks/useTypeSelector';
import useShowSnackbar from 'src/hooks/useShowSnackbar';
import { IHostFetch } from 'src/models/IHost';
import Icons from 'src/common/icons';

const HostTable: FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { getHosts, clearHostResult } = useActions();
  const {
    host: { hostList, isLoading, total, result }
  } = useTypedSelector(state => state);
  const hostStaffResult = useTypedSelector(state => state.hostStaff.result);
  useShowSnackbar(isLoading, result, clearHostResult, 'Host');

  const [selectedHost, setSelectedHost] = useState({} as IHostFetch);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openReviews, setOpenReviews] = useState(false);
  // const [openStaff, setOpenStaff] = useState(false);

  useEffect(() => {
    getHosts();
  }, []);

  useEffect(() => {
    (result.add ||
      result.update ||
      result.delete ||
      result.deleteStripe ||
      result.avatar ||
      hostStaffResult.add ||
      hostStaffResult.update ||
      hostStaffResult.delete) &&
      getHosts();
  }, [
    result.add,
    result.update,
    result.delete,
    result.avatar,
    result.deleteStripe,
    hostStaffResult.add,
    hostStaffResult.update,
    hostStaffResult.delete
  ]);

  // useEffect(() => {
  //   selectedHost &&
  //     result.all &&
  //     (hostStaffResult.add || hostStaffResult.update || hostStaffResult.delete) &&
  //     hostList.map(host => {
  //       host.id === selectedHost.id && setSelectedHost(host);
  //     });
  // }, [result.all]);

  useEffect(() => {
    if (result.deleteStripe) {
      enqueueSnackbar(`${result.deleteStripe}`, {
        variant: 'success'
      });
      clearHostResult();
    } else if (result.avatar === 200) {
      enqueueSnackbar('User photo successfully updated', {
        variant: 'success'
      });
      clearHostResult();
    }
  }, [result.deleteStripe, result.avatar]);

  const handleOpen = (host: IHostFetch, type: string) => {
    setSelectedHost(host);
    switch (type) {
      case 'edit':
        setOpenEdit(true);
        break;
      case 'delete':
        setOpenDelete(true);
        break;
      case 'review':
        setOpenReviews(true);
        break;
    }
  };

  const columns = useMemo(
    () => [
      {
        accessor: 'photo',
        Cell: ({
          row: {
            original: { photo }
          }
        }: any) => (
          <Avatar src={photo ? photo : '/broken-image.jpg'} sx={{ width: 30, height: 30 }} />
        ),
        width: 25
      },
      {
        Header: 'Name',
        accessor: 'name',
        Cell: ({
          row: {
            original: { first_name, last_name }
          }
        }: any) => {
          return <>{`${first_name} ${last_name}`}</>;
        }
      },
      {
        Header: 'Email',
        accessor: 'email',
        Cell: ({
          row: {
            original: { email }
          }
        }: any) => {
          return <>{email}</>;
        }
      },
      {
        Header: 'Phone',
        accessor: 'phone',
        Cell: ({
          row: {
            original: { phone }
          }
        }: any) => {
          return <>{phone}</>;
        }
      },
      {
        Header: 'Country',
        accessor: 'country',
        Cell: ({
          row: {
            original: { country }
          }
        }: any) => {
          return <>{country}</>;
        }
      },
      {
        Header: 'State',
        accessor: 'state',
        Cell: ({
          row: {
            original: { state }
          }
        }: any) => {
          return <>{state}</>;
        }
      },
      {
        Header: 'City',
        accessor: 'city',
        Cell: ({
          row: {
            original: { city }
          }
        }: any) => {
          return <>{city}</>;
        }
      },
      {
        Header: 'Home address',
        accessor: 'home_address',
        Cell: ({
          row: {
            original: { home_address }
          }
        }: any) => {
          return <>{home_address}</>;
        }
      },
      {
        Header: 'Nearest airport',
        accessor: 'nearest_airport',
        Cell: ({
          row: {
            original: { nearest_airport }
          }
        }: any) => {
          return <>{nearest_airport}</>;
        }
      },
      {
        Header: 'Company name',
        accessor: 'company_name',
        Cell: ({
          row: {
            original: { company_name }
          }
        }: any) => {
          return <>{company_name}</>;
        }
      },
      {
        Header: 'Host tag',
        accessor: 'host_tag',
        Cell: ({
          row: {
            original: { host_tag }
          }
        }: any) => {
          return <>{host_tag.join(', ')}</>;
        }
      },
      {
        Header: 'Stripe connect',
        accessor: 'stripe_connect_id',
        Cell: ({
          row: {
            original: { stripe_connect_id }
          }
        }: any) => {
          return <>{stripe_connect_id && `${stripe_connect_id.slice(0, 4)}...`}</>;
        }
      },
      {
        accessor: 'actions',
        Cell: ({ row: { original } }: any) => (
          <>
            <div className='flex_CC' style={{ justifyContent: 'right' }}>
              <div className='action-wrapper'>
                <FormIcon
                  icon={<ChatBubbleOutlineIcon classes='icon_stroke' sx={{ cursor: 'pointer' }} />}
                  handleClick={() => handleOpen(original, 'review')}
                  title='Feedback'
                />
                <FormIcon
                  icon={<Icons.Edit classes='icon_stroke' />}
                  handleClick={() => handleOpen(original, 'edit')}
                  title='Edit host'
                />
                <FormIcon
                  icon={<Icons.Delete classes='icon_stroke' />}
                  handleClick={() => handleOpen(original, 'delete')}
                  title='Delete host'
                />
              </div>
              {/* <Tooltip title='Staff'>
                <PeopleAltOutlinedIcon
                  className='icon_stroke'
                  strokeWidth={0}
                  sx={{ fontSize: '1.8rem' }}
                  onClick={() => handleOpen(original, 'staff')}
                />
              </Tooltip> */}
            </div>
          </>
        )
      }
    ],
    [hostList]
  );

  return (
    <>
      <HostModal open={openEdit} editHost={selectedHost} handleClose={() => setOpenEdit(false)} />
      <DeleteModalForm
        id={selectedHost.id}
        type='host'
        handleClose={() => setOpenDelete(false)}
        open={openDelete}
      />
      <ReviewListModal
        open={openReviews}
        handleClose={() => setOpenReviews(false)}
        hostId={selectedHost.id}
      />
      {/* <HostStaffModal
        open={openStaff}
        host={selectedHost}
        handleClose={() => setOpenStaff(false)}
      /> */}
      {isLoading.all ? (
        <CircularProgress
          size={200}
          style={{ position: 'fixed', top: '40%', left: '45%', zIndex: 100 }}
        />
      ) : hostList.length ? (
        <div className='order-table'>
          <Table.Base data={hostList} columns={columns} class='react-table' total={total!} />
        </div>
      ) : null}
    </>
  );
};
export default HostTable;
